<template>
  <section class="candidates-table-shell settings-panel">
    <div class="settings-org-section">
      <SettingsSectionHeader
        title="Data Import"
        subtitle="Upload spreadsheet export for cutover (admin only)"
        icon="pi-upload"
      >
        <template #actions>
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            size="small"
            severity="secondary"
            outlined
            :loading="loadingStatus"
            @click="refreshStatus"
          />
        </template>
      </SettingsSectionHeader>

      <div class="settings-org-section__body">
        <Message severity="info" :closable="false" class="settings-org-section__message">
          Export your Google Sheets workbook as <strong>.xlsx</strong>, upload here, review the
          validation report, then promote to merge data into the database. Take a database backup
          before promoting in production.
        </Message>

        <Message v-if="error" severity="error" :closable="false" class="settings-org-section__message">
          {{ error }}
        </Message>

        <div class="data-import-upload mb-4">
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-surface-700 dark:text-surface-300">File</span>

            <div v-if="pendingFile" class="data-import-pending">
              <FilePreviewIcon
                :file-name="pendingFile.name"
                :file-size="formatFileSize(pendingFile.size)"
                :mime-type="pendingFile.type"
                :file="pendingFile"
                :removable="!uploading"
                @remove="clearPendingFile"
              />
            </div>

            <FileDropzone
              ref="dropzoneRef"
              :disabled="uploadDisabled"
              :accept="IMPORT_UPLOAD_ACCEPT"
              :max-file-size="IMPORT_UPLOAD_MAX_BYTES"
              :dropzone-hint="IMPORT_UPLOAD_DROPZONE_HINT"
              :multiple="false"
              @select="onDropzoneSelect"
            />

            <div v-if="uploading" class="data-import-upload__status">
              <ProgressSpinner style="width: 1rem; height: 1rem" stroke-width="4" />
              <span>Uploading and validating…</span>
            </div>
          </div>
        </div>

        <div v-if="run" class="data-import-run">
          <div class="data-import-run__meta">
            <Tag severity="secondary" :value="run.run_id || run.runId" />
            <Tag
              :severity="statusSeverity(run.status)"
              :value="String(run.status || 'unknown').toUpperCase()"
            />
            <span v-if="run.original_filename" class="text-sm text-surface-600 dark:text-surface-300">
              {{ run.original_filename }}
            </span>
          </div>

          <div v-if="report?.counts" class="data-import-counts mt-3">
            <h4 class="text-sm font-semibold mb-2">Row counts</h4>
            <div class="data-import-counts__grid">
              <div v-for="(count, entity) in report.counts" :key="entity" class="data-import-counts__item">
                <span class="data-import-counts__label">{{ formatEntity(entity) }}</span>
                <span class="data-import-counts__value">{{ count }}</span>
              </div>
            </div>
          </div>

          <div v-if="report?.errors?.length" class="mt-3">
            <Message severity="error" :closable="false">
              {{ report.errors.length }} validation error(s) — fix spreadsheet and re-upload before promote.
            </Message>
            <ul class="data-import-list data-import-list--error mt-2">
              <li v-for="(item, idx) in report.errors.slice(0, 20)" :key="'e-' + idx">
                <strong>{{ item.entity }}</strong> {{ item.key }}: {{ item.message }}
              </li>
            </ul>
          </div>

          <div v-if="report?.warnings?.length" class="mt-3">
            <Message severity="warn" :closable="false">
              {{ report.warnings.length }} warning(s) — some rows may be skipped (e.g. unresolved driverKey).
            </Message>
            <ul class="data-import-list data-import-list--warn mt-2">
              <li v-for="(item, idx) in report.warnings.slice(0, 10)" :key="'w-' + idx">
                <strong>{{ item.entity }}</strong> {{ item.key }}: {{ item.message }}
              </li>
              <li v-if="report.warnings.length > 10" class="text-sm text-surface-500">
                …and {{ report.warnings.length - 10 }} more
              </li>
            </ul>
          </div>

          <div class="flex flex-wrap gap-2 mt-4">
            <Button
              label="Promote to database"
              icon="pi pi-database"
              severity="success"
              :disabled="!canPromote || promoting || run.status === 'promoted'"
              :loading="promoting"
              @click="confirmPromote"
            />
            <Button
              label="Re-validate"
              icon="pi pi-check-circle"
              severity="secondary"
              outlined
              :disabled="!runId || loadingStatus"
              :loading="loadingStatus"
              @click="revalidate"
            />
          </div>
        </div>

        <div v-else-if="!loadingStatus" class="settings-org-section__empty">
          <i class="pi pi-file-excel settings-org-section__empty-icon" aria-hidden="true" />
          <p>No import run yet. Upload an xlsx file to begin.</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import FileDropzone from '@/components/files_storage/FileDropzone.vue';
import FilePreviewIcon from '@/components/files_storage/FilePreviewIcon.vue';
import {
  IMPORT_UPLOAD_ACCEPT,
  IMPORT_UPLOAD_DROPZONE_HINT,
  IMPORT_UPLOAD_MAX_BYTES
} from '../../data/importUploadConfig';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import { useDataImport } from '../../composables/useDataImport';

defineOptions({ name: 'SettingsDataImportSection' });

defineProps({
  section: { type: Object, default: null }
});

const confirm = useConfirm();
const toast = useToast();
const pendingFile = ref(null);
const dropzoneRef = ref(null);

const {
  uploading,
  promoting,
  loadingStatus,
  error,
  run,
  report,
  canPromote,
  fetchStatus,
  uploadFile,
  validateRun,
  promoteRun
} = useDataImport();

const uploadDisabled = computed(() => uploading.value);

const runId = computed(() => run.value?.run_id || run.value?.runId || '');

const formatEntity = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusSeverity = (status) => {
  switch (status) {
    case 'promoted':
      return 'success';
    case 'validated':
      return 'info';
    case 'failed':
      return 'danger';
    default:
      return 'secondary';
  }
};

const refreshStatus = () => fetchStatus(runId.value || '');

const clearPendingFile = () => {
  if (uploading.value) return;
  pendingFile.value = null;
  dropzoneRef.value?.clear();
};

const onDropzoneSelect = async ({ files, errors }) => {
  if (uploadDisabled.value) return;

  if (errors.length) {
    error.value = errors.join('; ');
  }
  const file = files[0];
  if (!file) return;

  pendingFile.value = file;

  try {
    await uploadFile(file);
    pendingFile.value = null;
    dropzoneRef.value?.clear();
    toast.add({
      severity: 'success',
      summary: 'Upload complete',
      detail: 'Validation report is ready.',
      life: 4000
    });
    if (!errors.length) {
      error.value = '';
    }
  } catch {
    /* error ref set in composable; pendingFile kept for retry or detach */
  }
};

const revalidate = async () => {
  if (!runId.value) return;
  try {
    await validateRun(runId.value);
    toast.add({ severity: 'info', summary: 'Re-validated', life: 3000 });
  } catch {
    /* handled */
  }
};

const confirmPromote = () => {
  if (!runId.value || !canPromote.value) return;
  confirm.require({
    message:
      'Merge spreadsheet data into the production database? Ensure you have a recent database backup.',
    header: 'Confirm import promote',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Promote',
    acceptClass: 'p-button-success',
    accept: async () => {
      try {
        await promoteRun(runId.value, 'merge');
        toast.add({
          severity: 'success',
          summary: 'Import promoted',
          detail: 'Spreadsheet data merged successfully.',
          life: 5000
        });
      } catch {
        /* handled */
      }
    }
  });
};

onMounted(() => {
  fetchStatus().catch(() => {});
});
</script>

<style scoped>
.data-import-upload {
  max-width: 32rem;
}

.data-import-pending {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.data-import-upload__status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--p-text-secondary-color, #6b7280);
}

.data-import-run__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.data-import-counts__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
  gap: 0.5rem;
}

.data-import-counts__item {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: var(--p-surface-100);
}

.dark-mode .data-import-counts__item {
  background: color-mix(in srgb, var(--p-surface-800) 80%, transparent);
}

.data-import-counts__label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.75;
}

.data-import-counts__value {
  font-size: 1.125rem;
  font-weight: 600;
}

.data-import-list {
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.8125rem;
  max-height: 10rem;
  overflow-y: auto;
}

.data-import-list--error {
  color: var(--p-red-600);
}

.data-import-list--warn {
  color: var(--p-orange-700);
}
</style>
